import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Settings } from '@core/settings/schema.ts';
import {
  DEFAULT_SHORTCUT_BINDINGS,
  mergeShortcutBindings,
} from '@core/shortcuts/bindings.ts';
import { matchShortcut } from '@core/shortcuts/matchShortcut.ts';
import { activeCuesAt, effectiveMatchTime } from '@core/timeline.ts';
import type { VideoController } from '@ports/VideoController.ts';
import type { SubtitleStore, SubtitleSlot } from '../../state/subtitleStore.ts';
import {
  injectDictation,
  injectSpeakingLocks,
  removeDictation,
  type DictationCue,
} from './applyDictation.ts';
import { dictationPlayWordSeekMs, nextDictationHintChar } from './dictationShortcuts.ts';
import { playDictationClick } from './playDictationClick.ts';
import { findCueSeekTarget, type CueSeekKind } from '../../lib/cueSeek.ts';

/** Snapshot key so inject re-runs when cues arrive after practice mode is already on. */
function cueLoadKey(store: SubtitleStore): string {
  const main = store.getSlot('main');
  const second = store.getSlot('second');
  return [
    main.cues?.length ?? 0,
    main.status,
    main.selectedId ?? '',
    second.cues?.length ?? 0,
    second.status,
    second.selectedId ?? '',
  ].join(':');
}

function needsDictationInject(cues: DictationCue[]): boolean {
  return cues.some((c) => c.dictation === undefined || c.dictationRaw === undefined);
}

function needsSpeakingInject(cues: DictationCue[]): boolean {
  return cues.some((c) => c.dictation !== undefined || c.completed !== false || c.dictationRaw === undefined);
}

export type UseDictationOptions = {
  settings: Settings;
  video: VideoController | null;
  store: SubtitleStore;
  onInvalidate: () => void;
};

export type UseDictationResult = {
  activeCue: DictationCue | null;
  /** Live mask text for compose-band preview (updates as the user types). */
  maskPreview: string;
  /**
   * The non-practice track’s full cue text for compose (never masked).
   * Shown under the mask when the overlay is covered by the keyboard.
   */
  composeCompanion: string;
  inputValue: string;
  onInput: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  /**
   * Explicit leave-typing (compose Done / cue-adjacent blur).
   * Stops pause-at-end locker like extension focusout; keeps cue mask fill.
   */
  endTypingSession: () => void;
  onCompositionStart: () => void;
  onCompositionEnd: (value?: string) => void;
  /** Same logic as Alt+H shortcut. */
  onHint: () => void;
  /** Same logic as Alt+W shortcut. */
  onPlayWord: () => void;
  /**
   * Prev / replay / next cue while practice locking is on.
   * Matches extension dictation onKeyDown (clears subRef on prev/next so locker
   * does not yank playback back to the previous sentence).
   */
  navigateCue: (kind: CueSeekKind) => void;
  dictationSlot: SubtitleSlot;
  visible: boolean;
  /** Register the overlay input element so cue-click can focus it. */
  registerInput: (el: HTMLInputElement | null) => void;
  /** Extension: focus via subtitle click (no auto-focus on mode switch). */
  focusInput: () => void;
};

function resolveDictationSlot(settings: Settings): SubtitleSlot {
  // Extension popup uses `secondary`; Settings writes `secondary` (legacy `second` still accepted).
  const v = settings.dictationSubtitle;
  return v === 'second' || v === 'secondary' ? 'second' : 'main';
}

function otherSlot(slot: SubtitleSlot): SubtitleSlot {
  return slot === 'main' ? 'second' : 'main';
}

function skipNonMaskChars(cue: DictationCue): void {
  while (true) {
    const dictation = cue.dictation ?? '';
    const typedLen = cue.dictationTyped?.length ?? 0;
    const current = dictation.slice(typedLen)[0];
    if (current && current !== '_') {
      cue.dictationTyped = (cue.dictationTyped ?? '') + current;
    } else {
      break;
    }
  }
}

/** Recompose overlay mask: revealed prefix + remaining underscores (extension parity). */
function recomposeDictationMask(cue: DictationCue): void {
  const typed = cue.dictationTyped ?? '';
  const mask = cue.dictation ?? '';
  cue.dictation = typed + mask.slice(typed.length);
}

function findActiveGroup(cues: DictationCue[] | undefined, timeMs: number): DictationCue[] {
  if (!cues?.length) return [];
  return cues.filter((t) => t.start <= timeMs && timeMs <= t.end && !t.completed);
}

/** Plain (unmasked) cue line for compose context — main / second reference. */
function composeCueLine(
  store: SubtitleStore,
  slot: SubtitleSlot,
  nowMs: number,
): string {
  const state = store.getSlot(slot);
  if (state.view.hidden) return '';
  const matchMs = effectiveMatchTime(nowMs, state.view.delayMs);
  const cue =
    state.view.priority?.[0]
    ?? activeCuesAt(state.cues ?? [], matchMs)[0];
  if (!cue) return '';
  const strip = (raw: string) =>
    raw.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&lrm;/gi, '').trim();
  const primary = strip(cue.kuroshiro ?? cue.text ?? '');
  const translation = strip(cue.translation ?? '');
  if (state.view.dualMode) {
    return translation ? `${primary}\n${translation}` : primary;
  }
  if (cue.translated) return translation || primary;
  return primary;
}

export function useDictation({
  settings,
  video,
  store,
  onInvalidate,
}: UseDictationOptions): UseDictationResult {
  const dictationSlot = resolveDictationSlot(settings);
  const isSpeaking = settings.practiceMode === 'speaking';
  const isShadowing = settings.practiceMode === 'shadowing';
  const visible =
    settings.practiceMode === 'dictation'
    || isSpeaking
    || isShadowing;
  const isPractice = settings.practiceMode === 'dictation';
  /** Dictation + speaking pause at cue end; shadowing keeps continuous playback. */
  const pauseAtEndEnabled = isPractice || isSpeaking;
  const [activeCue, setActiveCue] = useState<DictationCue | null>(null);
  const [maskPreview, setMaskPreview] = useState('');
  const [composeCompanion, setComposeCompanion] = useState('');
  const [inputValue, setInputValue] = useState('');
  const subRef = useRef<DictationCue | null>(null);
  const lockerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusedRef = useRef(false);
  /** True while pause-at-end should run (survives soft-keyboard blur; Done clears it). */
  const lockingRef = useRef(false);
  /** True between practice navigateCue seek and its seeked — blocks re-pinning the old cue. */
  const navigatingRef = useRef(false);
  const composingRef = useRef(false);
  const onInputRef = useRef<(value: string) => void>(() => {});
  const inputElRef = useRef<HTMLInputElement | null>(null);
  const cuesEpoch = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => cueLoadKey(store),
    () => cueLoadKey(store),
  );

  const refreshComposeCompanion = useCallback(() => {
    if (!video || !visible || !isPractice) {
      setComposeCompanion('');
      return;
    }
    const nowMs = video.getCurrentTimeMs();
    setComposeCompanion(composeCueLine(store, otherSlot(dictationSlot), nowMs));
  }, [video, visible, isPractice, store, dictationSlot]);

  const registerInput = useCallback((el: HTMLInputElement | null) => {
    inputElRef.current = el;
  }, []);

  const focusInput = useCallback(() => {
    inputElRef.current?.focus({ preventScroll: true });
  }, []);

  const clearPriority = useCallback(() => {
    store.setViewState('main', { priority: undefined });
    store.setViewState('second', { priority: undefined });
  }, [store]);

  const pinPriority = useCallback(
    (slot: SubtitleSlot, group: DictationCue[]) => {
      store.setViewState(slot, { priority: group.length ? group : undefined });
      const other: SubtitleSlot = slot === 'main' ? 'second' : 'main';
      store.setViewState(other, { priority: undefined });
    },
    [store],
  );

  useEffect(() => {
    // Extension dictationInjector: mask only the practice track; strip the other.
    const active = dictationSlot;
    const other = otherSlot(active);
    const activeCues = store.getSlot(active).cues as DictationCue[] | undefined;
    const otherCues = store.getSlot(other).cues as DictationCue[] | undefined;

    if (!visible) {
      lockingRef.current = false;
      focusedRef.current = false;
      if (lockerRef.current) clearTimeout(lockerRef.current);
      lockerRef.current = null;
      for (const slot of ['main', 'second'] as const) {
        const cues = store.getSlot(slot).cues as DictationCue[] | undefined;
        if (cues?.length) removeDictation(cues);
      }
      subRef.current = null;
      setActiveCue(null);
      setMaskPreview('');
      setComposeCompanion('');
      setInputValue('');
      clearPriority();
    } else if (isSpeaking) {
      if (activeCues?.length && needsSpeakingInject(activeCues)) {
        injectSpeakingLocks(activeCues);
      }
      if (otherCues?.length) removeDictation(otherCues);
    } else if (isShadowing) {
      // Shadowing: record panel only — no pause-at-end locks on either track.
      lockingRef.current = false;
      if (lockerRef.current) {
        clearTimeout(lockerRef.current);
        lockerRef.current = null;
      }
      clearPriority();
      subRef.current = null;
      setActiveCue(null);
      for (const slot of ['main', 'second'] as const) {
        const cues = store.getSlot(slot).cues as DictationCue[] | undefined;
        if (cues?.length) removeDictation(cues);
      }
    } else {
      if (activeCues?.length && needsDictationInject(activeCues)) {
        // Only install masks when missing — never wipe in-progress fills on re-render.
        injectDictation(activeCues, settings);
      }
      if (otherCues?.length) removeDictation(otherCues);
    }
    onInvalidate();
  }, [
    visible,
    isSpeaking,
    isShadowing,
    dictationSlot,
    settings.dictationFilteredWords,
    settings.dictationIgnoreBracketedText,
    store,
    onInvalidate,
    cuesEpoch,
    clearPriority,
  ]);

  // When filter settings change, force full remask on the practice track only.
  const filterKey = `${settings.dictationFilteredWords}|${settings.dictationIgnoreBracketedText}`;
  const prevFilterRef = useRef(filterKey);
  useEffect(() => {
    if (!visible || !isPractice) {
      prevFilterRef.current = filterKey;
      return;
    }
    if (prevFilterRef.current === filterKey) return;
    prevFilterRef.current = filterKey;
    const activeCues = store.getSlot(dictationSlot).cues as DictationCue[] | undefined;
    const otherCues = store.getSlot(otherSlot(dictationSlot)).cues as DictationCue[] | undefined;
    if (activeCues?.length) injectDictation(activeCues, settings);
    if (otherCues?.length) removeDictation(otherCues);
    subRef.current = null;
    setActiveCue(null);
    setMaskPreview('');
    setComposeCompanion('');
    setInputValue('');
    clearPriority();
    onInvalidate();
  }, [filterKey, visible, isPractice, dictationSlot, settings, store, onInvalidate, clearPriority]);

  const acquireActiveCue = useCallback((): DictationCue | null => {
    if (!video) return null;
    const slotState = store.getSlot(dictationSlot);
    const timeMs = video.getCurrentTimeMs() + slotState.view.delayMs;
    const cues = slotState.cues as DictationCue[] | undefined;
    const group = findActiveGroup(cues, timeMs);
    const next = group.find((t) => !t.completed) ?? null;
    if (next) {
      pinPriority(dictationSlot, group);
      subRef.current = next;
      setActiveCue(next);
      setMaskPreview(next.dictation ?? next.text ?? '');
      refreshComposeCompanion();
    }
    return next;
  }, [video, store, dictationSlot, pinPriority, refreshComposeCompanion]);

  const locker = useCallback(() => {
    if (!video || !visible || !pauseAtEndEnabled || !lockingRef.current) return;
    if (lockerRef.current) clearTimeout(lockerRef.current);
    lockerRef.current = setTimeout(() => {
      if (!lockingRef.current || !video) return;
      const slotState = store.getSlot(dictationSlot);
      const delayMs = slotState.view.delayMs;
      const timeMs = video.getCurrentTimeMs() + delayMs;
      const cues = slotState.cues as DictationCue[] | undefined;
      let sub = subRef.current;

      // Scrub / foreign seek left the locked cue — release pin (extension clears
      // subRef on prev/next; native YT scrub never emits our seeked).
      if (sub && !sub.completed && (timeMs < sub.start - 250 || timeMs > sub.end + 400)) {
        clearPriority();
        subRef.current = null;
        setActiveCue(null);
        setMaskPreview('');
        setComposeCompanion('');
        setInputValue('');
        sub = null;
      }

      if (sub && !sub.completed && timeMs >= sub.end - 1) {
        if (!video.isPaused()) {
          video.pause();
          locker();
          return;
        }
        // Seek back to cue start once; skip while getCurrentTime still reports the end
        // (avoids a rapid seek loop that looks like the video jumping).
        if (timeMs > sub.start + 50) {
          video.seekMs(sub.start + 1);
        }
        locker();
        return;
      }

      if (!sub) {
        // Wait for navigateCue / scrub seek to settle before pinning a new cue.
        if (navigatingRef.current) {
          locker();
          return;
        }
        const group = findActiveGroup(cues, timeMs);
        const next = group.find((t) => !t.completed) ?? null;
        if (next) {
          pinPriority(dictationSlot, group);
          subRef.current = next;
          setActiveCue(next);
          setMaskPreview(next.dictation ?? next.text ?? '');
          setInputValue('');
          refreshComposeCompanion();
        }
      } else {
        refreshComposeCompanion();
      }

      if (sub?.completed) {
        clearPriority();
        subRef.current = null;
        setActiveCue(null);
        setMaskPreview('');
        setComposeCompanion('');
        setInputValue('');
      }

      locker();
    }, 100);
  }, [
    video,
    visible,
    pauseAtEndEnabled,
    store,
    dictationSlot,
    pinPriority,
    clearPriority,
    refreshComposeCompanion,
  ]);

  // Practice-track switch (settings dictationSubtitle): remask + refresh compose panel.
  const prevDictationSlotRef = useRef(dictationSlot);
  useEffect(() => {
    if (prevDictationSlotRef.current === dictationSlot) return;
    prevDictationSlotRef.current = dictationSlot;
    if (!visible) {
      setComposeCompanion('');
      return;
    }
    const activeCues = store.getSlot(dictationSlot).cues as DictationCue[] | undefined;
    const otherCues = store.getSlot(otherSlot(dictationSlot)).cues as DictationCue[] | undefined;
    if (isSpeaking) {
      if (activeCues?.length) injectSpeakingLocks(activeCues);
      if (otherCues?.length) removeDictation(otherCues);
    } else if (isShadowing) {
      for (const slot of ['main', 'second'] as const) {
        const cues = store.getSlot(slot).cues as DictationCue[] | undefined;
        if (cues?.length) removeDictation(cues);
      }
    } else {
      if (activeCues?.length) injectDictation(activeCues, settings);
      if (otherCues?.length) removeDictation(otherCues);
      setComposeCompanion('');
    }
    subRef.current = null;
    setActiveCue(null);
    setMaskPreview('');
    setInputValue('');
    clearPriority();
    onInvalidate();
    if (pauseAtEndEnabled && lockingRef.current) {
      if (lockerRef.current) clearTimeout(lockerRef.current);
      lockerRef.current = null;
      acquireActiveCue();
      locker();
    } else if (isPractice) {
      refreshComposeCompanion();
    }
  }, [
    dictationSlot,
    visible,
    isSpeaking,
    isShadowing,
    isPractice,
    pauseAtEndEnabled,
    settings,
    store,
    onInvalidate,
    clearPriority,
    acquireActiveCue,
    refreshComposeCompanion,
    locker,
  ]);

  // Extension: locker starts on focus for dictation and speaking (not shadowing).
  const onFocus = useCallback(() => {
    focusedRef.current = true;
    if (!pauseAtEndEnabled) return;
    lockingRef.current = true;
    if (!subRef.current) acquireActiveCue();
    locker();
  }, [pauseAtEndEnabled, acquireActiveCue, locker]);

  const onBlur = useCallback(() => {
    // Soft-keyboard dismiss must not kill pause-at-end or wipe mask progress.
    // Compose "Done" / cue-adjacent blur call endTypingSession instead.
    focusedRef.current = false;
  }, []);

  /** Extension onFocusout: stop locker + drop active cue pin; keep typed mask fill. */
  const endTypingSession = useCallback(() => {
    focusedRef.current = false;
    lockingRef.current = false;
    if (lockerRef.current) clearTimeout(lockerRef.current);
    lockerRef.current = null;
    clearPriority();
    subRef.current = null;
    setActiveCue(null);
    setMaskPreview('');
    setComposeCompanion('');
    setInputValue('');
  }, [clearPriority]);

  const onCompositionStart = useCallback(() => {
    composingRef.current = true;
  }, []);

  const onCompositionEnd = useCallback((value?: string) => {
    composingRef.current = false;
    // Extension: compositionend dispatches synthetic input so the final
    // composed string is committed (dictation.js).
    if (value != null) onInputRef.current(value);
  }, []);

  const onInput = useCallback(
    (value: string) => {
      if (composingRef.current) return;
      if (!isPractice) {
        setInputValue(value);
        return;
      }

      playDictationClick(settings.dictationTypingSoundVolume);

      let sub = subRef.current;
      if (!sub || !sub.dictationRaw) {
        sub = acquireActiveCue();
      }
      if (!sub || !sub.dictationRaw || !sub.dictation) {
        setInputValue(value);
        return;
      }

      skipNonMaskChars(sub);
      const typed = sub.dictationTyped ?? '';
      // Remainder of the expected plain text (extension: dictationRaw.replace(typed, '')).
      const currentStr = typed && sub.dictationRaw.startsWith(typed)
        ? sub.dictationRaw.slice(typed.length)
        : sub.dictationRaw.replace(typed, '');

      if (
        value
        && currentStr.toLowerCase().startsWith(value.toLowerCase())
        && currentStr.slice(0, value.length)
      ) {
        sub.dictationTyped = typed + currentStr.slice(0, value.length);
        setInputValue('');
      } else {
        setInputValue(value);
      }

      skipNonMaskChars(sub);
      recomposeDictationMask(sub);
      setMaskPreview(sub.dictation ?? '');
      refreshComposeCompanion();

      if (sub.dictationTyped === sub.dictationRaw) {
        sub.completed = true;
        clearPriority();
        video?.play();
      }

      // In-place cue.dictation mutation — force overlay paint while paused (extension parity).
      onInvalidate();
    },
    [
      isPractice,
      video,
      onInvalidate,
      acquireActiveCue,
      clearPriority,
      settings.dictationTypingSoundVolume,
      refreshComposeCompanion,
    ],
  );
  onInputRef.current = onInput;

  const runHint = useCallback(() => {
    let sub = subRef.current;
    if (!sub || !sub.dictationRaw) {
      sub = acquireActiveCue();
    }
    if (!sub) return;
    const ch = nextDictationHintChar(sub);
    if (ch) onInput(ch);
  }, [acquireActiveCue, onInput]);

  const runPlayWord = useCallback(() => {
    if (!video) return;
    let sub = subRef.current;
    if (!sub || !sub.dictationRaw) {
      sub = acquireActiveCue();
    }
    if (!sub) return;
    const seekMs = dictationPlayWordSeekMs(sub);
    if (seekMs == null) return;
    if (lockerRef.current) clearTimeout(lockerRef.current);
    void video.play();
    video.seekMs(seekMs);
    locker();
  }, [video, acquireActiveCue, locker]);

  /**
   * Extension dictation.js seekPrev/Repeat/Next while the practice input is active:
   * clear locker timer; on prev/next drop subRef + priority so pause-at-end cannot
   * re-seek to the old cue; play + seek; restart locker (re-acquires at new time).
   */
  const navigateCue = useCallback(
    (kind: CueSeekKind) => {
      if (!video || !visible) return;
      if (lockerRef.current) clearTimeout(lockerRef.current);
      lockerRef.current = null;

      if (kind === 'prev' || kind === 'next') {
        clearPriority();
        subRef.current = null;
        setActiveCue(null);
        setMaskPreview('');
        setComposeCompanion('');
        setInputValue('');
      }

      const slotState = store.getSlot(dictationSlot);
      const cues = slotState.cues;
      if (!cues?.length) return;
      const timeMs = video.getCurrentTimeMs() + slotState.view.delayMs;
      const target = findCueSeekTarget(cues, timeMs, kind);
      if (!target) return;

      navigatingRef.current = true;
      if (pauseAtEndEnabled) {
        lockingRef.current = true;
      }
      void video.play();
      video.seekMs(target.start);
      // Re-arm locker after seek settles (see seeked handler). Fallback if seeked is late.
      window.setTimeout(() => {
        if (!navigatingRef.current) return;
        navigatingRef.current = false;
        if (pauseAtEndEnabled) locker();
      }, 600);
    },
    [video, visible, pauseAtEndEnabled, store, dictationSlot, clearPriority, locker],
  );

  // Progress-bar / navigateCue seeked: drop pin when outside the locked cue, then re-arm.
  useEffect(() => {
    if (!video || !visible) return undefined;
    const onSeeked = () => {
      if (!lockingRef.current) return;

      if (navigatingRef.current) {
        navigatingRef.current = false;
        locker();
        return;
      }

      const sub = subRef.current;
      if (!sub || sub.completed) {
        locker();
        return;
      }
      const delayMs = store.getSlot(dictationSlot).view.delayMs;
      const timeMs = video.getCurrentTimeMs() + delayMs;
      const stillInCue = timeMs >= sub.start - 25 && timeMs <= sub.end + 25;
      if (stillInCue) return;
      clearPriority();
      subRef.current = null;
      setActiveCue(null);
      setMaskPreview('');
      setComposeCompanion('');
      setInputValue('');
      locker();
    };
    return video.on('seeked', onSeeked);
  }, [video, visible, store, dictationSlot, clearPriority, locker]);

  // Alt+H hint / Alt+W play-word / Alt+A·S·D cue nav — work while practice is active
  // (do not use isEditableKeyboardTarget / enableShortcuts gate — extension parity).
  useEffect(() => {
    if (!visible) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      const bindings = mergeShortcutBindings(
        settings.shortcutBindings ?? DEFAULT_SHORTCUT_BINDINGS,
      );
      const hintHit = isPractice && matchShortcut(event, bindings.dictationHint);
      const playHit = isPractice && matchShortcut(event, bindings.dictationPlayWord);
      const prevHit = matchShortcut(event, bindings.seekPrev);
      const repeatHit = matchShortcut(event, bindings.seekRepeat);
      const nextHit = matchShortcut(event, bindings.seekNext);
      if (!hintHit && !playHit && !prevHit && !repeatHit && !nextHit) return;

      event.preventDefault();
      event.stopPropagation();

      if (hintHit) {
        runHint();
        return;
      }
      if (playHit) {
        runPlayWord();
        return;
      }
      if (prevHit) {
        navigateCue('prev');
        return;
      }
      if (repeatHit) {
        navigateCue('repeat');
        return;
      }
      if (nextHit) {
        navigateCue('next');
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [
    visible,
    isPractice,
    settings.shortcutBindings,
    runHint,
    runPlayWord,
    navigateCue,
  ]);

  return {
    activeCue,
    maskPreview,
    composeCompanion,
    inputValue,
    onInput,
    onFocus,
    onBlur,
    endTypingSession,
    onCompositionStart,
    onCompositionEnd,
    onHint: runHint,
    onPlayWord: runPlayWord,
    navigateCue,
    dictationSlot,
    visible,
    registerInput,
    focusInput,
  };
}
